import React from 'react';
import { Node } from '@xyflow/react';
import {
  PlusIcon,
  ArrowPathIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  HandRaisedIcon,
  CodeBracketIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import type { NodeType } from '../../types/releaseFlow';

interface NodeToolbarProps {
  onAddNode: (nodeType: NodeType) => void;
  onAutoLayout: () => void;
  onSave?: () => void;
  onValidate?: () => void;
  selectedNodes: Node[];
}

const nodeTypeConfigs = [
  {
    type: 'pipeline' as NodeType,
    label: 'Pipeline',
    icon: PlayIcon,
    description: 'Deploy to environment',
    color: 'blue',
  },
  {
    type: 'approval' as NodeType,
    label: 'Approval',
    icon: CheckCircleIcon,
    description: 'Manual approval gate',
    color: 'green',
  },
  {
    type: 'condition' as NodeType,
    label: 'Condition',
    icon: QuestionMarkCircleIcon,
    description: 'Conditional logic',
    color: 'yellow',
  },
  {
    type: 'manual' as NodeType,
    label: 'Manual',
    icon: HandRaisedIcon,
    description: 'Manual intervention',
    color: 'red',
  },
  {
    type: 'script' as NodeType,
    label: 'Script',
    icon: CodeBracketIcon,
    description: 'Run custom script',
    color: 'purple',
  },
  {
    type: 'notification' as NodeType,
    label: 'Notification',
    icon: BellIcon,
    description: 'Send notification',
    color: 'pink',
  },
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200',
    red: 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200',
    purple: 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200',
    pink: 'bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-200',
    gray: 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200',
  };
  return colors[color as keyof typeof colors] || colors.gray;
};

export const NodeToolbar: React.FC<NodeToolbarProps> = ({
  onAddNode,
  onAutoLayout,
  onSave,
  onValidate,
  selectedNodes,
}) => {
  const [showNodeTypes, setShowNodeTypes] = React.useState(false);

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Release Flow Editor</h3>
        <div className="text-xs text-gray-500">
          {selectedNodes.length > 0 && `${selectedNodes.length} selected`}
        </div>
      </div>

      {/* Add Node Section */}
      <div className="space-y-2">
        <button
          onClick={() => setShowNodeTypes(!showNodeTypes)}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Node</span>
        </button>

        {showNodeTypes && (
          <div className="space-y-1 border-t pt-2">
            {nodeTypeConfigs
              .filter(config => config.type === 'pipeline') // Only show pipeline nodes
              .map((config) => {
              const IconComponent = config.icon;
              return (
                <button
                  key={config.type}
                  onClick={() => {
                    onAddNode(config.type);
                    setShowNodeTypes(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm transition-colors border ${getColorClasses(
                    config.color
                  )}`}
                  title={config.description}
                >
                  <IconComponent className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{config.label}</div>
                    <div className="text-xs opacity-75">{config.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-2 border-t pt-2">
        <button
          onClick={onAutoLayout}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span>Auto Layout</span>
        </button>

        {onValidate && (
          <button
            onClick={onValidate}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-md text-sm font-medium transition-colors"
          >
            <CheckCircleIcon className="w-4 h-4" />
            <span>Validate</span>
          </button>
        )}

        {onSave && (
          <button
            onClick={onSave}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            <DocumentArrowUpIcon className="w-4 h-4" />
            <span>Save</span>
          </button>
        )}
      </div>

      {/* Selected Node Info */}
      {selectedNodes.length > 0 && (
        <div className="border-t pt-2">
          <div className="text-xs font-medium text-gray-700 mb-2">Selected Nodes:</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {selectedNodes.map((node) => (
              <div
                key={node.id}
                className="text-xs bg-gray-50 rounded px-2 py-1 flex items-center space-x-2"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                <div className="truncate">
                  <div className="font-medium">{node.data.label as string}</div>
                  <div className="text-gray-500">{node.data.nodeType as string}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};